import { inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class MobileDashboardService implements OnDestroy {
  showOpenLink = false;
  link = '';
  error = '';
  private router = inject(Router, { optional: true });
  private navigation?: Subscription;
  private readonly lastDashboardKey = 'mobileDashboard:lastPath';

  get isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  get standalone() {
    return window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true;
  }

  start() {
    if (this.navigation || !this.router) return;
    this.navigation = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) this.remember(event.urlAfterRedirects);
    });
    if (this.router.navigated) this.remember(this.router.url);
  }

  dashboardPath(value: string): string | undefined {
    try {
      const url = new URL(value.trim(), window.location.origin);
      if (url.origin !== window.location.origin || url.username || url.password) return;
      const match = /^\/(action|report-new-breakdown)\/([1-9]\d*)\/?$/.exec(url.pathname);
      if (match && Number.isSafeInteger(Number(match[2]))) return `/${match[1]}/${match[2]}`;
    } catch { /* Invalid pasted links stay on the current dashboard. */ }
    return;
  }

  async openLink() {
    const path = this.dashboardPath(this.link);
    if (!path) {
      this.error = 'Paste a Fleet or Service Provider breakdown link from this site.';
      return;
    }
    try {
      if (this.dashboardPath(this.router?.url || '') === path || await this.router?.navigateByUrl(path)) {
        this.showOpenLink = false;
        this.link = this.error = '';
      } else this.error = 'Could not open this breakdown. Please try again.';
    } catch { this.error = 'Could not open this breakdown. Please try again.'; }
  }

  private remember(value: string) {
    if (!this.isIOS || !this.router) return;
    const url = new URL(value, window.location.origin);
    const path = this.dashboardPath(value);
    if (this.standalone) {
      // Only an icon launch may restore the last incident. Explicit links/push clicks win.
      if ((url.pathname === '/' || url.searchParams.get('homeScreen') === '1') &&
          url.searchParams.get('openChat') !== 'true') {
        let previous: string | undefined;
        try { previous = this.dashboardPath(localStorage.getItem(this.lastDashboardKey) || ''); } catch { /* Storage is optional. */ }
        const target = previous || path;
        if (target) { void this.router.navigateByUrl(target, { replaceUrl: true }); return; }
      }
      if (url.searchParams.has('homeScreen')) {
        url.searchParams.delete('homeScreen');
        void this.router.navigateByUrl(url.pathname + url.search, { replaceUrl: true });
        return;
      }
      if (path) {
        try { localStorage.setItem(this.lastDashboardKey, path); } catch { /* The installed URL still works without storage. */ }
      }
    } else if (path) {
      // With no manifest start_url, iOS installs the current document URL, including this marker.
      if (url.searchParams.get('homeScreen') !== '1') {
        url.searchParams.set('homeScreen', '1');
        void this.router.navigateByUrl(url.pathname + url.search, { replaceUrl: true });
        return;
      }
      const manifest = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
      if (manifest) {
        // Reprocess the manifest after SPA navigation so installation captures this incident.
        const href = new URL('manifest.webmanifest', document.baseURI);
        href.searchParams.set('dashboard', path);
        manifest.href = href.href;
      }
    }
  }

  ngOnDestroy() { this.navigation?.unsubscribe(); }
}
